/**
 * Team Onboarding Wizard Component
 *
 * Phase 5: Collaboration & Teams - Team Onboarding
 * Multi-step wizard guiding new organizations through setup.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../auth';
import {
  getProgress,
  initOnboarding,
  completeStep,
  skipStep,
  getStepGuidance,
  getChecklist,
  getStepIcon,
  getStepStatusColor,
  getEstimatedTime,
} from '../../services/onboardingApi';
import { Button, Card, Badge, Toast } from '../ui';
import {
  Building,
  Users,
  Settings,
  Github,
  BarChart3,
  Circle,
  ChevronRight,
  ChevronDown,
  Check,
  SkipForward,
  Clock,
  Loader2,
  AlertCircle,
  CheckCircle2,
  HelpCircle,
  Sparkles,
} from 'lucide-react';

// Icon mapping
const IconMap = {
  Building,
  Users,
  Settings,
  Github,
  BarChart3,
  Circle,
};

// =============================================================================
// Progress Ring Component
// =============================================================================

const ProgressRing = ({ progress, size = 120, strokeWidth = 8 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="url(#teamProgressGradient)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-500"
        />
        <defs>
          <linearGradient id="teamProgressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#8B5CF6" />
            <stop offset="100%" stopColor="#06B6D4" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold text-white">{Math.round(progress)}%</span>
        <span className="text-xs text-white/60">Complete</span>
      </div>
    </div>
  );
};

ProgressRing.propTypes = {
  progress: PropTypes.number.isRequired,
  size: PropTypes.number,
  strokeWidth: PropTypes.number,
};

// =============================================================================
// Step Card Component
// =============================================================================

const StepCard = ({
  step,
  isExpanded,
  onToggle,
  onComplete,
  onSkip,
  guidance,
  loadingGuidance,
  isProcessing,
}) => {
  const IconComponent = IconMap[getStepIcon(step.id)] || Circle;
  const statusColor = getStepStatusColor(step);

  return (
    <div
      className={`
        rounded-xl border transition-all duration-200
        ${step.completed
          ? 'bg-success-500/5 border-success-500/20'
          : step.current
            ? 'bg-primary-500/10 border-primary-500/30 shadow-lg shadow-primary-500/10'
            : 'bg-black/20 border-white/10'
        }
      `}
    >
      {/* Step Header */}
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center gap-4 text-left"
        disabled={step.completed}
      >
        {/* Step Icon */}
        <div className={`p-3 rounded-xl border ${statusColor}`}>
          {step.completed ? (
            <Check className="w-5 h-5" />
          ) : (
            <IconComponent className="w-5 h-5" />
          )}
        </div>

        {/* Step Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className={`font-medium ${step.completed ? 'text-success-400' : 'text-white'}`}>
              {step.name}
            </h3>
            {step.required && !step.completed && (
              <Badge variant="error" size="sm">Required</Badge>
            )}
            {step.completed && (
              <Badge variant="success" size="sm">Done</Badge>
            )}
            {step.skipped && (
              <Badge variant="secondary" size="sm">Skipped</Badge>
            )}
          </div>
          {step.description && (
            <p className="text-sm text-white/60 mt-0.5 truncate">{step.description}</p>
          )}
        </div>

        {/* Expand/Collapse */}
        {!step.completed && (
          <div className="text-white/40">
            {isExpanded ? (
              <ChevronDown className="w-5 h-5" />
            ) : (
              <ChevronRight className="w-5 h-5" />
            )}
          </div>
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && !step.completed && (
        <div className="px-4 pb-4 border-t border-white/10 mt-0">
          <div className="pt-4 space-y-4">
            {/* Guidance Section */}
            {loadingGuidance ? (
              <div className="flex items-center gap-2 text-white/60">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Loading guidance...</span>
              </div>
            ) : guidance ? (
              <div className="space-y-3">
                {/* Why This Step */}
                {guidance.why && (
                  <div className="p-3 rounded-lg bg-secondary-500/10 border border-secondary-500/20">
                    <div className="flex items-start gap-2">
                      <HelpCircle className="w-4 h-4 text-secondary-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-secondary-400">Why this step?</p>
                        <p className="text-sm text-white/70 mt-1">{guidance.why}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Instructions */}
                {guidance.instructions && guidance.instructions.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-white/80 mb-2">Instructions:</p>
                    <ol className="space-y-2">
                      {guidance.instructions.map((instruction, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-white/70">
                          <span className="w-5 h-5 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center text-xs shrink-0 mt-0.5">
                            {index + 1}
                          </span>
                          <span>{instruction}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* Tips */}
                {guidance.tips && guidance.tips.length > 0 && (
                  <div className="p-3 rounded-lg bg-warning-500/10 border border-warning-500/20">
                    <p className="text-sm font-medium text-warning-400 mb-2 flex items-center gap-1">
                      <Sparkles className="w-4 h-4" />
                      Pro Tips
                    </p>
                    <ul className="space-y-1">
                      {guidance.tips.map((tip, index) => (
                        <li key={index} className="text-sm text-white/70 flex items-start gap-2">
                          <span className="text-warning-400 mt-1">-</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-white/60">
                Complete this step to continue your organization setup.
              </p>
            )}

            {/* Action Buttons */}
            <div className="flex items-center gap-3 pt-2">
              <Button
                variant="primary"
                onClick={onComplete}
                disabled={isProcessing}
                className="flex items-center gap-2"
              >
                {isProcessing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                Mark Complete
              </Button>
              {!step.required && (
                <Button
                  variant="ghost"
                  onClick={onSkip}
                  disabled={isProcessing}
                  className="flex items-center gap-2 text-white/60 hover:text-white"
                >
                  <SkipForward className="w-4 h-4" />
                  Skip for now
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

StepCard.propTypes = {
  step: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    required: PropTypes.bool,
    completed: PropTypes.bool,
    skipped: PropTypes.bool,
    current: PropTypes.bool,
  }).isRequired,
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  guidance: PropTypes.shape({
    why: PropTypes.string,
    instructions: PropTypes.arrayOf(PropTypes.string),
    tips: PropTypes.arrayOf(PropTypes.string),
  }),
  loadingGuidance: PropTypes.bool,
  isProcessing: PropTypes.bool,
};

// =============================================================================
// Main Wizard Component
// =============================================================================

const TeamOnboardingWizard = ({ orgId, onComplete }) => {
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [expandedStep, setExpandedStep] = useState(null);
  const [guidance, setGuidance] = useState({});
  const [loadingGuidance, setLoadingGuidance] = useState({});
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // Load progress
  const loadProgress = useCallback(async () => {
    if (!user || !orgId) return;

    try {
      const [progressData, checklistData] = await Promise.all([
        getProgress(user, orgId),
        getChecklist(user, orgId),
      ]);
      setProgress(progressData);
      setChecklist(checklistData);

      // Auto-expand current step
      if (progressData.current_step) {
        setExpandedStep(progressData.current_step);
      }
    } catch (err) {
      console.error('Failed to load onboarding progress:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user, orgId]);

  // Initialize onboarding if needed
  useEffect(() => {
    const init = async () => {
      if (!user || !orgId) return;

      try {
        // Try to get progress first
        await loadProgress();
      } catch {
        // If no progress exists, initialize
        try {
          await initOnboarding(user, orgId);
          await loadProgress();
        } catch (err) {
          console.error('Failed to initialize onboarding:', err);
          setError(err.message);
          setLoading(false);
        }
      }
    };

    init();
  }, [user, orgId, loadProgress]);

  // Load guidance when step is expanded
  const handleToggleStep = async (stepId) => {
    if (expandedStep === stepId) {
      setExpandedStep(null);
      return;
    }

    setExpandedStep(stepId);

    // Load guidance if not already loaded
    if (!guidance[stepId] && !loadingGuidance[stepId]) {
      setLoadingGuidance(prev => ({ ...prev, [stepId]: true }));
      try {
        const guidanceData = await getStepGuidance(user, stepId);
        setGuidance(prev => ({ ...prev, [stepId]: guidanceData }));
      } catch (err) {
        console.error('Failed to load guidance:', err);
      } finally {
        setLoadingGuidance(prev => ({ ...prev, [stepId]: false }));
      }
    }
  };

  // Complete step
  const handleComplete = async (stepId) => {
    setProcessing(true);
    try {
      const newProgress = await completeStep(user, orgId, stepId);
      setProgress(newProgress);
      setToast({ type: 'success', message: 'Step completed!' });

      // Reload checklist
      const checklistData = await getChecklist(user, orgId);
      setChecklist(checklistData);

      // Check if all done
      if (newProgress.is_complete) {
        setToast({ type: 'success', message: 'Onboarding complete! Welcome aboard!' });
        if (onComplete) {
          setTimeout(() => onComplete(), 1500);
        }
      } else if (newProgress.current_step) {
        setExpandedStep(newProgress.current_step);
      }
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    } finally {
      setProcessing(false);
    }
  };

  // Skip step
  const handleSkip = async (stepId) => {
    setProcessing(true);
    try {
      const newProgress = await skipStep(user, orgId, stepId);
      setProgress(newProgress);
      setToast({ type: 'info', message: 'Step skipped. You can complete it later.' });

      // Reload checklist
      const checklistData = await getChecklist(user, orgId);
      setChecklist(checklistData);

      // Move to next step
      if (newProgress.current_step) {
        setExpandedStep(newProgress.current_step);
      }
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    } finally {
      setProcessing(false);
    }
  };

  // Calculate remaining steps
  const getRemainingSteps = () => {
    if (!progress?.steps) return [];
    return progress.steps.filter(s => !s.completed && !s.skipped);
  };

  if (loading) {
    return (
      <Card className="p-8">
        <div className="flex flex-col items-center justify-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
          <p className="text-white/60">Loading onboarding...</p>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-8">
        <div className="flex flex-col items-center justify-center gap-4">
          <AlertCircle className="w-8 h-8 text-error-400" />
          <p className="text-white/60">{error}</p>
          <Button variant="ghost" onClick={loadProgress}>
            Try Again
          </Button>
        </div>
      </Card>
    );
  }

  const remainingSteps = getRemainingSteps();

  return (
    <div className="space-y-6">
      {/* Header with Progress */}
      <Card className="p-6">
        <div className="flex items-center gap-6">
          <ProgressRing progress={progress?.progress_percent || 0} />

          <div className="flex-1">
            <h2 className="text-xl font-semibold text-white mb-2">
              Team Setup
            </h2>
            <p className="text-white/60 mb-4">
              Complete these steps to get your organization set up and unlock the full power of portfolio intelligence.
            </p>

            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2 text-white/60">
                <Check className="w-4 h-4 text-success-400" />
                <span>
                  {progress?.completed_count || 0} of {progress?.total_steps || 0} steps complete
                </span>
              </div>
              {remainingSteps.length > 0 && (
                <div className="flex items-center gap-2 text-white/60">
                  <Clock className="w-4 h-4 text-primary-400" />
                  <span>{getEstimatedTime(remainingSteps)} remaining</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Checklist Status */}
      {checklist && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 rounded-xl bg-black/20 border border-white/10">
            <div className="text-2xl font-bold text-white">
              {checklist.stats?.member_count || 0}
            </div>
            <div className="text-sm text-white/60">Team Members</div>
          </div>
          <div className="p-4 rounded-xl bg-black/20 border border-white/10">
            <div className="text-2xl font-bold text-white">
              {checklist.stats?.project_count || 0}
            </div>
            <div className="text-sm text-white/60">Projects</div>
          </div>
          <div className="p-4 rounded-xl bg-black/20 border border-white/10">
            <div className="text-2xl font-bold text-white">
              {checklist.stats?.analyzed_count || 0}
            </div>
            <div className="text-sm text-white/60">Analyzed</div>
          </div>
          <div className="p-4 rounded-xl bg-black/20 border border-white/10">
            <div className="text-2xl font-bold text-white">
              {checklist.stats?.skill_count || 0}
            </div>
            <div className="text-sm text-white/60">Skills Tracked</div>
          </div>
        </div>
      )}

      {/* Steps List */}
      <div className="space-y-3">
        {progress?.steps?.map((step) => (
          <StepCard
            key={step.id}
            step={step}
            isExpanded={expandedStep === step.id}
            onToggle={() => handleToggleStep(step.id)}
            onComplete={() => handleComplete(step.id)}
            onSkip={() => handleSkip(step.id)}
            guidance={guidance[step.id]}
            loadingGuidance={loadingGuidance[step.id]}
            isProcessing={processing}
          />
        ))}
      </div>

      {/* Completion Celebration */}
      {progress?.is_complete && (
        <Card className="p-8 bg-gradient-to-br from-success-500/20 to-success-500/20 border-success-500/30">
          <div className="flex flex-col items-center text-center gap-4">
            <div className="p-4 rounded-full bg-success-500/20 border border-success-500/30">
              <CheckCircle2 className="w-12 h-12 text-success-400" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-white mb-2">
                Team Setup Complete!
              </h3>
              <p className="text-white/70">
                Your organization is all set up and ready to go.
                Start exploring your team's portfolio intelligence!
              </p>
            </div>
            <Button
              variant="primary"
              onClick={onComplete}
              className="mt-2"
            >
              Go to Dashboard
            </Button>
          </div>
        </Card>
      )}

      {/* Toast Notifications */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

TeamOnboardingWizard.propTypes = {
  /** Organization ID */
  orgId: PropTypes.number.isRequired,
  /** Callback when onboarding is completed */
  onComplete: PropTypes.func,
};

export default TeamOnboardingWizard;
